import { useEffect, useId, useRef, useState, type MouseEvent } from 'react';
import { IconCheck, IconCopy, IconRefresh } from '@/components/Icons';
import { PasswordInput } from '@/components/PasswordInput';
import { useAppConfig } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import { formatAccessMessage, type AccessDetails } from '@/lib/access';
import { copyText } from '@/lib/clipboard';
import { isValidLogin } from '@/lib/login';
import { generatePassword, isStrongPassword } from '@/lib/password';

/** Quanto tempo o botão mostra "Copiado". */
const COPIED_FEEDBACK_MS = 2000;

interface PasswordFieldProps {
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** O resto do acesso, que "Copiar login" junta com a senha. */
  access: Pick<AccessDetails, 'name' | 'login' | 'email'>;
}

/**
 * Senha que o administrador define para um membro: gera uma senha forte,
 * mostra ou esconde o que está no campo, e copia o acesso inteiro — usuário,
 * e-mail e senha — numa mensagem pronta para mandar à pessoa.
 *
 * Os botões ficam fora do <label>: dentro dele, o nome deles entraria no nome
 * do campo lido pelo leitor de tela.
 */
export function PasswordField({ label, hint, value, onChange, placeholder, access }: PasswordFieldProps) {
  const id = useId();
  const hintId = `${id}-dica`;
  const toast = useToast();
  const appConfig = useAppConfig();
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  const canCopy = isValidLogin(access.login) && isStrongPassword(value);

  function change(next: string) {
    onChange(next);
    setCopied(false);
  }

  function generate() {
    change(generatePassword());
    // Senha gerada fica à vista: quem gerou confere o que vai mandar.
    setVisible(true);
  }

  async function copy(event: MouseEvent<HTMLButtonElement>) {
    const container = event.currentTarget.closest<HTMLElement>('[role="dialog"]') ?? undefined;
    // O endereço oficial (CORS_ORIGINS), não o domínio em que o administrador está.
    const url = appConfig.data?.appUrl ?? window.location.origin;
    const text = formatAccessMessage({ ...access, password: value, url });
    if (!(await copyText(text, container))) {
      toast.error('Não foi possível copiar. Selecione o usuário e a senha e copie à mão.');
      return;
    }
    setCopied(true);
    window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  }

  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>{label}</label>
      <PasswordInput
        id={id}
        value={value}
        onChange={change}
        visible={visible}
        onVisibleChange={setVisible}
        autoComplete="new-password"
        placeholder={placeholder}
        aria-describedby={hintId}
      />
      <div className="password-actions">
        <button type="button" className="btn btn-sm btn-ghost" onClick={generate}>
          <IconRefresh /> {value ? 'Gerar outra' : 'Gerar senha'}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={(event) => void copy(event)}
          disabled={!canCopy}
          title={canCopy ? 'Copia usuário, e-mail e senha, prontos para mandar' : 'Preencha o usuário e uma senha válida'}
        >
          {copied ? <IconCheck /> : <IconCopy />} {copied ? 'Copiado' : 'Copiar login'}
        </button>
        <span className="sr-only" aria-live="polite">{copied ? 'Login copiado.' : ''}</span>
      </div>
      <span id={hintId} className="field-hint">{hint}</span>
    </div>
  );
}
